// src/features/admin/components/NotificationDetailModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { getErrorMessage, isVersionConflict, conflictCurrentVersion } from "utils/errors";

const GOLD = "#f3dfae";
const NAVY = "#1e2754";

type Props = {
  id: number;
  onClose: () => void;
  onActionComplete?: () => void | Promise<void>;
};

type AnyNotif = Record<string, any>;

const prettyJson = (v: any) => {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const tryGetDetail = async (id: number): Promise<AnyNotif> => {
  const mod: any = await import("api/admin");

  const candidates = [
    mod.adminGetNotification,
    mod.adminGetNotificationDetail,
    mod.adminGetNotificationById,
    mod.adminFetchNotification,
    mod.getNotification,
    mod.getNotificationDetail
  ].filter(Boolean);

  if (candidates.length === 0) {
    return { id };
  }

  let lastErr: any = null;
  for (const fn of candidates) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fn(id);
    } catch (e: any) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Failed to load notification detail");
};

const tryRetry = async (n: AnyNotif) => {
  const mod: any = await import("api/admin");
  if (!mod.adminRetryNotification) throw new Error("adminRetryNotification not found");
  return await mod.adminRetryNotification(n.id, n.lock_version);
};

const tryAcknowledge = async (n: AnyNotif) => {
  const mod: any = await import("api/admin");
  if (!mod.adminAcknowledgeNotification) throw new Error("adminAcknowledgeNotification not found");
  return await mod.adminAcknowledgeNotification(n.id, n.lock_version);
};

const NotificationDetailModal: React.FC<Props> = ({ id, onClose, onActionComplete }) => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [notif, setNotif] = useState<AnyNotif | null>(null);
  const [acting, setActing] = useState<"retry" | "ack" | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const d = await tryGetDetail(id);
      setNotif(d);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to load notification"));
      setNotif(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const statusLabel = useMemo(() => {
    const s = String(notif?.status ?? "").toUpperCase();
    return s || "—";
  }, [notif]);

  const created = useMemo(() => {
    const d = notif?.created_date || notif?.created_at || notif?.created;
    try {
      return d ? new Date(d).toLocaleString() : "—";
    } catch {
      return String(d || "—");
    }
  }, [notif]);

  const kind = useMemo(() => {
    return (
      notif?.type ||
      notif?.kind ||
      notif?.notification_type ||
      notif?.template ||
      notif?.name ||
      "Notification"
    );
  }, [notif]);

  const onRetry = async () => {
    if (!notif) return;
    setErr("");
    setActing("retry");
    try {
      const updated = await tryRetry(notif);
      setNotif(updated);
      if (onActionComplete) await onActionComplete();
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(current ? `Version conflict (current v${current}). Reloading…` : "Version conflict. Reloading…");
        await load();
      } else {
        setErr(getErrorMessage(e, "Failed to retry"));
      }
    } finally {
      setActing(null);
    }
  };

  const onAcknowledge = async () => {
    if (!notif) return;
    setErr("");
    setActing("ack");
    try {
      const updated = await tryAcknowledge(notif);
      setNotif(updated);
      if (onActionComplete) await onActionComplete();
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(current ? `Version conflict (current v${current}). Reloading…` : "Version conflict. Reloading…");
        await load();
      } else {
        setErr(getErrorMessage(e, "Failed to acknowledge"));
      }
    } finally {
      setActing(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Notification details"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
      />

      <div
        className={[
          "relative w-full max-w-3xl",
          "max-h-[90vh] overflow-y-auto",
          "rounded-2xl bg-white shadow-2xl",
          "border"
        ].join(" ")}
        style={{ borderColor: `${GOLD}B3` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b" style={{ borderColor: `${GOLD}66` }}>
          <div className="px-6 py-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-ui text-xs uppercase tracking-[0.18em] text-slate-500">
                Notification
              </div>
              <div className="mt-1 flex items-center gap-3 flex-wrap">
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#151A3A]">
                  #{id}
                </h2>
                <span
                  className="inline-flex items-center rounded-full px-4 py-2 font-ui font-normal uppercase tracking-wide text-xs text-white"
                  style={{ backgroundColor: NAVY }}
                >
                  {statusLabel}
                </span>
              </div>
              <div className="mt-2 font-body text-slate-700">
                <span className="font-semibold">{String(kind)}</span>{" "}
                <span className="text-slate-500">·</span>{" "}
                <span className="text-slate-600">{created}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className={[
                "inline-flex items-center justify-center rounded-full",
                "border border-slate-200 bg-white",
                "px-5 py-2.5",
                "text-xs uppercase tracking-wide text-[#151A3A]",
                "hover:bg-slate-50 transition-colors font-ui font-normal"
              ].join(" ")}
            >
              Close
            </button>
          </div>
        </div>

        <div className="px-6 py-6">
          {loading ? (
            <div className="font-body text-slate-600">Loading…</div>
          ) : (
            <>
              {err && (
                <div className="mb-5 border border-red-300 bg-red-50 text-red-700 p-3 rounded-2xl font-body text-sm">
                  {err}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
                    Recipient
                  </div>
                  <div className="mt-2 font-body text-slate-800 break-words">
                    {String(notif?.to || notif?.recipient || notif?.email || notif?.user_email || "—")}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
                    Attempts
                  </div>
                  <div className="mt-2 font-body text-slate-800">
                    {typeof notif?.attempts === "number"
                      ? notif.attempts
                      : typeof notif?.retry_count === "number"
                      ? notif.retry_count
                      : "—"}
                  </div>
                </div>
              </div>

              {(notif?.last_error || notif?.error || notif?.failure_reason) && (
                <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 p-4">
                  <div className="font-ui text-xs uppercase tracking-wide text-rose-700">
                    Last error
                  </div>
                  <div className="mt-2 font-body text-rose-900 break-words">
                    {String(notif?.last_error || notif?.error || notif?.failure_reason)}
                  </div>
                </div>
              )}

              <div className="mt-6 space-y-5">
                <div className="rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="font-ui text-xs uppercase tracking-wide text-slate-700">
                      Payload
                    </div>
                  </div>
                  <pre className="p-4 text-xs leading-relaxed overflow-x-auto text-slate-800">
                    {prettyJson(notif?.payload ?? notif?.request ?? notif?.data ?? notif)}
                  </pre>
                </div>

                {(notif?.response || notif?.result) && (
                  <div className="rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="font-ui text-xs uppercase tracking-wide text-slate-700">
                        Response
                      </div>
                    </div>
                    <pre className="p-4 text-xs leading-relaxed overflow-x-auto text-slate-800">
                      {prettyJson(notif?.response ?? notif?.result)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={onRetry}
                  disabled={acting != null || !notif}
                  className={[
                    "inline-flex items-center justify-center gap-3",
                    "rounded-full px-6 py-3",
                    "text-[#151A3A] font-ui font-normal uppercase tracking-wide",
                    "hover:opacity-95 transition-opacity",
                    "disabled:opacity-50"
                  ].join(" ")}
                  style={{ backgroundColor: GOLD }}
                >
                  <span className="text-xs sm:text-sm whitespace-nowrap">
                    {acting === "retry" ? "Retrying…" : "Retry"}
                  </span>
                  <span aria-hidden="true" className="text-base leading-none">
                    →
                  </span>
                </button>

                <button
                  type="button"
                  onClick={onAcknowledge}
                  disabled={acting != null || !notif}
                  className={[
                    "inline-flex items-center justify-center rounded-full",
                    "border border-slate-200 bg-white px-6 py-3",
                    "text-[#151A3A] font-ui font-normal uppercase tracking-wide",
                    "hover:bg-slate-50 transition-colors",
                    "disabled:opacity-50"
                  ].join(" ")}
                >
                  {acting === "ack" ? "Acknowledging…" : "Acknowledge"}
                </button>
              </div>

              <div className="mt-6 font-ui text-xs uppercase tracking-wide text-slate-500">
                Tip: press Esc to close
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationDetailModal;
